import React, { useEffect, useMemo } from 'react';
import * as Portals from 'react-reverse-portal';
import PropTypes from 'prop-types';
import { GoogleProvider } from '../context/GoogleProvider';

const DEFAULT_OPTIONS = {
  portalNodeName: 'portalNode',
  props: () => {},
  skip: () => {},
};

function withMapReversePortal(PortaledComponent, options = {}) {
  return (WrappedComponent) => {
    const optionsWithDefaults = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    function WrappedPortalComponent(props) {
      /**
       * We use reverse portals to render maps in this project to avoid a known
       * memory leak in the Google Maps JavaScript API. Google recommend re-using
       * the same map instance rather than mounting/unmounting and creating new
       * instances for this reason. A reverse portal allows us to move around
       * the same instance of the Map to avoid this issue, in this case across different modals.
       */
      const portalNode = useMemo(
        () =>
          Portals.createHtmlPortalNode({
            attributes: {
              style: 'width: 100%',
              id: 'map-portal-container',
            },
          }),
        []
      );

      // Dispose of the portal when we unmount this component
      useEffect(
        () => () => {
          portalNode.unmount();
        },
        [portalNode]
      );

      const { portalNodeName } = optionsWithDefaults;

      /**
       * If we should skip rendering the portal, then just return the wrapped component
       * with a null portal. Example use-case for this is not rendering a map portal
       */
      const shouldSkipRender = optionsWithDefaults?.skip?.(props) ?? false;
      if (shouldSkipRender) {
        return <WrappedComponent {...props} {...{ [portalNodeName]: null }} />;
      }

      return (
        <>
          <GoogleProvider
            googleMapsApiKey={props.labels?.googleApiKey}
            googleMapStyleId={props.labels?.googleMapStyleId}
          >
            <Portals.InPortal node={portalNode}>
              <PortaledComponent />
            </Portals.InPortal>
          </GoogleProvider>
          <WrappedComponent {...props} portalNode={portalNode} />
        </>
      );
    }

    WrappedPortalComponent.propTypes = {
      labels: PropTypes.shape({
        googleApiKey: PropTypes.string.isRequired,
        googleMapStyleId: PropTypes.string.isRequired,
      }).isRequired,
    };

    return WrappedPortalComponent;
  };
}

export default withMapReversePortal;
